import * as requests from "../API/requests";

const MILESTONES = {
  1: "You've started!",
  3: "Three months strong!",
  6: "Half a year of consistency!",
  12: "A full year — incredible discipline.",
  24: "Two years of investing. Unstoppable.",
};

const streakStore = {
  namespaced: true,

  state: () => ({
    streaks: [],
    history: null,
    isLoading: false,
    historyLoading: false,
    restoreLoading: false,
    restoreError: null,
  }),

  mutations: {
    setStreaks(state, payload) {
      state.streaks = payload;
    },
    setHistory(state, payload) {
      state.history = payload;
    },
    setLoading(state, payload) {
      state.isLoading = payload;
    },
    setHistoryLoading(state, payload) {
      state.historyLoading = payload;
    },
    setRestoreLoading(state, payload) {
      state.restoreLoading = payload;
    },
    setRestoreError(state, payload) {
      state.restoreError = payload;
    },
  },

  actions: {
    async loadStreaks({ commit }) {
      commit("setLoading", true);
      try {
        const data = await requests.getStreaksRequest();
        commit("setStreaks", data);
        return data;
      } catch (e) {
        console.error("Failed to load streaks:", e);
        return [];
      } finally {
        commit("setLoading", false);
      }
    },

    async loadHistory({ commit }, { code, fromDate, toDate } = {}) {
      commit("setHistoryLoading", true);
      try {
        const streakCode = code || "MONTHLY_INVESTING";
        const data = await requests.getStreakHistoryRequest(streakCode, fromDate, toDate);
        commit("setHistory", data);
        return data;
      } catch (e) {
        console.error("Failed to load streak history:", e);
        return null;
      } finally {
        commit("setHistoryLoading", false);
      }
    },

    refreshStreaks({ dispatch }) {
      return new Promise((resolve) => {
        setTimeout(() => {
          dispatch("loadStreaks").then(resolve);
        }, 1500);
      });
    },

    async restoreStreak({ commit, dispatch }) {
      commit("setRestoreLoading", true);
      commit("setRestoreError", null);
      try {
        const data = await requests.restoreStreakRequest("MONTHLY_INVESTING");
        await dispatch("loadStreaks");
        return data;
      } catch (e) {
        const msg = e?.response?.data?.message || "Failed to restore streak.";
        commit("setRestoreError", msg);
        throw e;
      } finally {
        commit("setRestoreLoading", false);
      }
    },
  },

  getters: {
    monthlyStreak(state) {
      return state.streaks.find((s) => s.code === "MONTHLY_INVESTING") || null;
    },

    streakStatus(state, getters) {
      const streak = getters.monthlyStreak;
      if (!streak) return "never_invested";
      if (streak.current_length === 0 && streak.longest_length > 0) return "broken";
      if (streak.current_length === 1) return "just_started";
      if (streak.current_length > 1) return "active";
      return "never_invested";
    },

    milestoneMessage(state, getters) {
      const streak = getters.monthlyStreak;
      if (!streak || streak.current_length === 0) return null;
      return MILESTONES[streak.current_length] || null;
    },

    /**
     * Returns the full month name (e.g. "November") if the current or previous
     * month was saved by the investment window. Returns null otherwise.
     * Reads new field first, falls back to deprecated field during transition.
     */
    windowUsedRecently(state, getters) {
      const streak = getters.monthlyStreak;
      if (!streak) return null;

      const windowMonths = streak.investment_window_months || streak.grace_used_months || [];
      if (windowMonths.length === 0) return null;

      const now = new Date();
      const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prevKey = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;

      const recentWindow = windowMonths.find(
        (m) => m === currentKey || m === prevKey
      );
      if (!recentWindow) return null;

      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
      ];
      const monthIndex = parseInt(recentWindow.split("-")[1], 10) - 1;
      return monthNames[monthIndex];
    },

    /**
     * True when the user has an active streak but hasn't deposited this month
     * and we're past the 15th. Mutually exclusive with windowUsedRecently.
     */
    isAtRisk(state, getters) {
      const streak = getters.monthlyStreak;
      if (!streak || streak.current_length === 0) return false;
      if (getters.windowUsedRecently) return false;

      const now = new Date();
      if (now.getDate() <= 15) return false;

      if (!streak.last_activity_date) return true;
      const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      return !streak.last_activity_date.startsWith(currentKey);
    },

    /**
     * Formatted investment window deadline string (e.g. "Apr 5").
     */
    windowDeadline(state, getters) {
      const streak = getters.monthlyStreak;
      if (!streak || !getters.isAtRisk) return null;

      const windowDays = streak.investment_window_days || streak.grace_days || 0;
      if (windowDays === 0) return null;

      const now = new Date();
      const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const deadline = new Date(nextMonthStart.getTime() + windowDays * 86400000);

      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${monthNames[deadline.getMonth()]} ${deadline.getDate()}`;
    },

    /** Whether the user has deposited in the current month. */
    investedThisMonth(state, getters) {
      const streak = getters.monthlyStreak;
      if (!streak || !streak.last_activity_date) return false;
      const now = new Date();
      const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      return streak.last_activity_date.startsWith(currentKey);
    },

    /** Whether the user can restore and has invested this month. */
    canRestore(state, getters) {
      const streak = getters.monthlyStreak;
      if (!streak) return false;
      return !!streak.can_restore;
    },

    /** Number of restores remaining this year. */
    restoresRemaining(state, getters) {
      const streak = getters.monthlyStreak;
      if (!streak) return 0;
      return (streak.max_restores_per_year || 2) - (streak.restores_used_this_year || 0);
    },

    /** Monthly totals from history endpoint. */
    monthlyTotals(state) {
      return state.history?.monthly_totals || {};
    },

    /** Months whose total met the min_amount threshold. */
    qualifyingMonths(state) {
      return state.history?.qualifying_months || [];
    },
  },
};

export default streakStore;
